// Spec 26 time-attendance — yasal yıllık ücretli izin hakkı (4857 m.53-54).
//
// Hak YILDÖNÜMÜNDE TOPLU verilir (sahip kararı K1): işçi bir hizmet yılını
// tamamlayınca o yılın hakkı doğar, önce değil. Aylık orantılı tahakkuk yasal
// izinde kullanılmaz; bu yüzden hakkı tarih ve yaştan hesaplayan bu modül
// tek doğruluk kaynağıdır — süpürme işi, demo tohumu ve testler aynı
// fonksiyonu çağırır, ikinci bir basamak listesi tutulmaz.
//
// Kurum hakkı yalnız YUKARI değiştirebilir. Buradaki değerler yasal TABANDIR;
// sözleşmeden gelen ek gün ayrı bir türde/kuralda izlenir (K2), tabanı düşüren
// hiçbir ayar bu fonksiyonun sonucunu değiştirmez.

import { HOURS_PER_WORKING_DAY } from "./leave-duration.ts";

/** `entity_leave_type.accrual_kind` değeri: yasal yıllık izin türü. */
export const STATUTORY_ACCRUAL_KIND = "statutory_tr";
/** `entity_leave_type.statutory_ref` değeri: 4857 m.53 yıllık ücretli izin. */
export const STATUTORY_ANNUAL_LEAVE_REF = "4857/53";
/** Yasa bir hizmet yılı dolmadan hak doğurmaz: yasal türün kıdem tabanı. */
export const STATUTORY_MIN_TENURE_MONTHS = 12;

/**
 * Yasal yıllık izin türü mü (4857 m.53). İki işaretten biri yeter: tohumlanan tür
 * ikisini de taşır, ama İK'nın elle açtığı bir tür yalnız `statutory_ref` ya da
 * yalnız `accrual_kind` ile işaretlenmiş olabilir — korumanın bir alanın
 * doldurulmasına bağlı olması, alanı boş bırakmayı korumadan çıkmanın yolu
 * yapardı. Sunucu koruması ve ekran (kilitli alanlar) aynı yüklemi kullanır.
 */
export function isStatutoryAnnualLeaveType(type: {
  readonly accrualKind: string;
  readonly statutoryRef: string | null;
}): boolean {
  return type.accrualKind === STATUTORY_ACCRUAL_KIND || type.statutoryRef === STATUTORY_ANNUAL_LEAVE_REF;
}

/**
 * Talep kapısının ve hak verme geçişinin OKUDUĞU asgari kıdem. Yasal türde
 * kayıtlı değer ne olursa olsun 12'dir: kanun hakkı ilk yıldönümünde doğurur ve
 * kurum bunu yalnız işçi lehine oynatabilir, yani süreyi uzatamaz. Kaydı
 * okumak yerine buradan çözmek, doğrulamanın olmadığı dönemden kalan (ör. 24
 * yazılmış) bir satırın ilk yıl hakkını sessizce kapatmasını da önler.
 */
export function effectiveMinTenureMonths(type: {
  readonly accrualKind: string;
  readonly statutoryRef: string | null;
  readonly minTenureMonths: number;
}): number {
  return isStatutoryAnnualLeaveType(type) ? STATUTORY_MIN_TENURE_MONTHS : type.minTenureMonths;
}

/** Hangi kural sonucu belirledi: kıdem basamağı ya da yaş tabanı. */
export type AnnualLeaveRuleBasis = "tenure" | "age";

export interface AnnualLeaveEntitlementInput {
  /** Tamamlanan hizmet yılı: 1 = ilk yıldönümü. 1'in altında hak doğmaz. */
  readonly serviceYearIndex: number;
  /**
   * İşçinin YILDÖNÜMÜ GÜNÜNDEKİ yaşı; doğum tarihi bilinmiyorsa null. Bilinmeyen
   * yaş kıdem basamağına düşer — yaş tabanını uydurmak, bilmediğimiz bir işçiye
   * yasal olmayan bir gün sayısı yazmak olurdu.
   */
  readonly ageOnAnniversary: number | null;
  /**
   * İşçi YILDÖNÜMÜ GÜNÜNDE yer altı işinde mi (4857 m.53, LV-09). Kayıtlı bir
   * olgudur (`underground_work_from/until`), türetilmez; bilinmiyorsa ya da
   * verilmediyse yer altı sayılmaz — bilmediğimiz bir işçiye dört gün eklemek
   * kaydı olmayan bir hakkı yazmak olurdu.
   */
  readonly undergroundOnAnniversary?: boolean;
  /**
   * Basamağın OKUNDUĞU hizmet yılı: onaylı önceki hizmet birleştirmesiyle (4857 m.54,
   * LV-06) çalışılan yıla eklenmiş toplam. Yalnız basamağı yükseltir: hak `serviceYearIndex`
   * ile doğar (m.53 aynı işyerinde bir yıl ister), birleştirilen süre ise hangi basamağın
   * uygulanacağını belirler. `serviceYearIndex`ten küçük değer yok sayılır — birleştirme
   * hizmeti azaltamaz. Verilmezse `serviceYearIndex` okunur.
   */
  readonly tierServiceYears?: number;
}

export interface AnnualLeaveEntitlement {
  /** Yasal sonuç: kıdem basamağı/yaş tabanı + yer altı eki. */
  readonly days: number;
  readonly hours: number;
  /** Yer altı ekinden ÖNCEKİ tabanı belirleyen kural; ek `undergroundBonusDays`te ayrı okunur. */
  readonly ruleBasis: AnnualLeaveRuleBasis;
  /** `days`in içindeki yer altı eki: 0 ya da UNDERGROUND_BONUS_DAYS. */
  readonly undergroundBonusDays: number;
  /** Basamağın okunduğu hizmet yılı (>= `serviceYearIndex`); hak doğmadıysa 0. */
  readonly tierServiceYears: number;
}

/** 18 yaş ve altı ile 50 yaş ve üstü için yasal alt sınır (m.53/2). */
export const AGE_FLOOR_LEAVE_DAYS = 20;
export const AGE_FLOOR_YOUNG_MAX = 18;
export const AGE_FLOOR_OLD_MIN = 50;

/**
 * Yer altı işlerinde çalışanların yıllık izin süresine eklenen gün (4857 m.53,
 * 6552 s. K. m.5 ile 10/9/2014'te eklenen cümle: "dörder gün arttırılarak
 * uygulanır"). Kanun "yer altı işi"ni bu maddede tanımlamaz: hangi işlerin
 * sayılacağı kayıt sahibinin (İK) beyanıdır — dayanak teyit bekliyor.
 */
export const UNDERGROUND_BONUS_DAYS = 4;

/** Yer altı çalışma dönemi: `until` dahil, null = süren dönem. */
export interface UndergroundWorkPeriod {
  readonly from: string;
  readonly until: string | null;
}

/**
 * Yıldönümü günü kayıtlı yer altı döneminin içinde mi (uçlar dahil). Değerlendirme
 * DÖNEM TARİHİNE göredir, bugünkü duruma değil: süpürme kaçırılmış yılları
 * geriye doğru yazabilir ve bugün yer altına yeni geçmiş bir işçiye eski yılların
 * ekini vermek ya da yer altından çıkmış birinin geçmiş yıllarını eksiltmek
 * yanlış olurdu.
 */
export function undergroundWorkCovers(period: UndergroundWorkPeriod | null, on: string): boolean {
  if (period === null) return false;
  return period.from <= on && (period.until === null || on <= period.until);
}

/**
 * Kıdem basamağı: 1-5 yıl (5 dahil) 14, 5 yıldan fazla 15 yıldan az 20, 15 yıl
 * ve üstü 26 gün. Hak yıldönümlerinde verildiği için "5 yıldan fazla" 6.
 * yıldönümüdür: tam 5. yıldönümünde işçi beş yılını yeni doldurmuştur ve
 * kanun "beş yıl dahil" der.
 */
export function tenureLadderDays(serviceYearIndex: number): number {
  if (!Number.isInteger(serviceYearIndex) || serviceYearIndex < 1) return 0;
  if (serviceYearIndex <= 5) return 14;
  if (serviceYearIndex < 15) return 20;
  return 26;
}

/**
 * Bir hizmet yılının yasal izin hakkı. Kıdem basamağı ile yaş tabanının BÜYÜĞÜ
 * alınır — kanun "en az" der, ikisini toplamaz — ve yer altı çalışıyorsa üstüne
 * dört gün eklenir.
 *
 * Yer altı eki YAŞ TABANINDAN SONRA eklenir: 50 yaşında yer altı işçisi kıdem
 * basamağı 14 iken 20 + 4 = 24 alır. Metin sırası ("dörder gün arttırılarak
 * uygulanır. Ancak … yirmi günden az olamaz") ikinci bir okumaya da izin verir:
 * önce 14 + 4 = 18, sonra taban ile 20. İki okuma yalnız yaş tabanı kıdem
 * basamağını yükselttiği (basamak 14, ≤ 18 ya da ≥ 50 yaş) yer altı çalışanında
 * ayrışır. Buradaki okuma iki yorumda da yasal tabanın ALTINA düşmez; diğeri
 * doğru olsaydı bu okuma bir gün sayısını işçi lehine fazla yazardı, tersi ise
 * kanunun altında bırakırdı. Sahip teyidi bekliyor.
 */
export function annualLeaveEntitlement(input: AnnualLeaveEntitlementInput): AnnualLeaveEntitlement {
  // Hakkın doğup doğmadığı YALNIZ çalışılan hizmet yılına bakar; birleştirilen süre
  // ilk yıldönümünü öne çekmez ve `serviceYearIndex < 1` hâlâ hak doğurmaz.
  if (tenureLadderDays(input.serviceYearIndex) === 0) {
    return { days: 0, hours: 0, ruleBasis: "tenure", undergroundBonusDays: 0, tierServiceYears: 0 };
  }
  const tierServiceYears = Math.max(input.serviceYearIndex, input.tierServiceYears ?? 0);
  const tenureDays = tenureLadderDays(tierServiceYears);

  const age = input.ageOnAnniversary;
  const ageFloorApplies =
    age !== null && (age <= AGE_FLOOR_YOUNG_MAX || age >= AGE_FLOOR_OLD_MIN);
  const ageLifts = ageFloorApplies && AGE_FLOOR_LEAVE_DAYS > tenureDays;
  const baseDays = ageLifts ? AGE_FLOOR_LEAVE_DAYS : tenureDays;
  const undergroundBonusDays = input.undergroundOnAnniversary === true ? UNDERGROUND_BONUS_DAYS : 0;
  const days = baseDays + undergroundBonusDays;
  return {
    days,
    hours: daysToLeaveHours(days),
    ruleBasis: ageLifts ? "age" : "tenure",
    undergroundBonusDays,
    tierServiceYears,
  };
}

/**
 * Gün → saat. İzin günü 7,5 saattir (K4): talep süresi de aynı sabitle
 * hesaplandığı için bir yıllık hakkın tamamı kullanıldığında bakiye tam sıfıra
 * iner; iki taraf farklı bölenle çalışsa artıklı bir bakiye kalırdı.
 */
export function daysToLeaveHours(days: number): number {
  return Math.round(days * HOURS_PER_WORKING_DAY * 100) / 100;
}

interface IsoParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseIsoDate(iso: string): IsoParts {
  const match = ISO_DATE.exec(iso);
  if (match === null) throw new Error(`annual-leave-entitlement: not a YYYY-MM-DD date: ${iso}`);
  const parts = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
  const probe = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  if (
    probe.getUTCFullYear() !== parts.year ||
    probe.getUTCMonth() !== parts.month - 1 ||
    probe.getUTCDate() !== parts.day
  ) {
    throw new Error(`annual-leave-entitlement: not a calendar date: ${iso}`);
  }
  return parts;
}

function formatIso(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * `iso` tarihinden `years` yıl sonrası; hedef ayda o gün yoksa ayın son günü.
 * 29 Şubat'ta işe giren biri artık olmayan yılda 28 Şubat'ta yıldönümüne
 * ulaşır — 1 Mart'a taşırmak hakkı bir gün geciktirirdi, ve yaş için de aynı
 * kural geçerli olsun ki iki hesap aynı takvimi görsün.
 */
export function addYearsClamped(iso: string, years: number): string {
  const { year, month, day } = parseIsoDate(iso);
  const targetYear = year + years;
  const daysInTargetMonth = new Date(Date.UTC(targetYear, month, 0)).getUTCDate();
  return formatIso(targetYear, month, Math.min(day, daysInTargetMonth));
}

/**
 * `iso` tarihinden `months` ay sonrası; hedef ayda o gün yoksa ayın son günü (31
 * Ağustos + 6 ay = 28/29 Şubat). `addYearsClamped` ile aynı kural: iki hesap aynı
 * takvimi görür.
 */
export function addMonthsClamped(iso: string, months: number): string {
  const { year, month, day } = parseIsoDate(iso);
  const total = year * 12 + (month - 1) + months;
  const targetYear = Math.floor(total / 12);
  const targetMonth = (total % 12) + 1;
  const daysInTargetMonth = new Date(Date.UTC(targetYear, targetMonth, 0)).getUTCDate();
  return formatIso(targetYear, targetMonth, Math.min(day, daysInTargetMonth));
}

const MS_PER_DAY = 86_400_000;

/** `iso` tarihinden `days` gün sonrası (negatif = önce). Takvim günüdür; saat dilimi yok. */
export function addDays(iso: string, days: number): string {
  const { year, month, day } = parseIsoDate(iso);
  const shifted = new Date(Date.UTC(year, month - 1, day) + days * MS_PER_DAY);
  return formatIso(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}

/** `from`dan `to`ya takvim günü farkı; `to` önceyse negatif. */
export function daysBetween(from: string, to: string): number {
  const a = parseIsoDate(from);
  const b = parseIsoDate(to);
  return Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / MS_PER_DAY);
}

/**
 * Sözleşme ek gününün devir süresinin (`carryover_expires_months`) üst sınırı.
 * Devir yıldönümünde damgalanır ve süre sonu bir SONRAKİ yıldönümünden önce ya da
 * onunla aynı gün gelmek zorundadır: süre sonunda bakiyeden yakılacak saat, yalnız
 * o yıla devredenlerden ve o yılın hakkından oluşur; iki yıldönümü aşan bir süre
 * hangi saatin hangi yıldan kaldığını bakiye toplamından ayıramazdı.
 */
export const MAX_CARRYOVER_EXPIRES_MONTHS = 12;

/** `serviceYearIndex`. hizmet yılının tamamlandığı gün (yıldönümü). */
export function serviceAnniversary(seniorityDate: string, serviceYearIndex: number): string {
  return addYearsClamped(seniorityDate, serviceYearIndex);
}

/** `from` ile `to` arasında TAMAMLANAN tam yıl; `to`, `from`dan önceyse 0. */
export function completedYearsBetween(from: string, to: string): number {
  let years = parseIsoDate(to).year - parseIsoDate(from).year;
  if (years <= 0) return 0;
  // Ham yıl farkı yıldönümü henüz gelmediyse bir fazladır; ISO tarihler
  // sözlük sırasıyla kıyaslanır, ayrı bir ay/gün karşılaştırması gerekmez.
  if (addYearsClamped(from, years) > to) years -= 1;
  return Math.max(0, years);
}

/** İşçinin `on` günündeki yaşı (doğum günü o gün dahil sayılır). */
export function ageOnDate(dateOfBirth: string, on: string): number {
  return completedYearsBetween(dateOfBirth, on);
}

/** `today` itibarıyla yıldönümü gelmiş hizmet yılları: 1..N. */
export function dueServiceYearIndexes(seniorityDate: string, today: string): number[] {
  const completed = completedYearsBetween(seniorityDate, today);
  return Array.from({ length: completed }, (_, i) => i + 1);
}
